import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTheme } from '../../theme/ThemeContext';
import { closeOrder, closePosition, getOrderTypeLabel } from '../../services/platform/TradingStore';
import platformService from '../../services/platform/platformService';
import { ActivityIcon, ClockIcon } from '../../helpers/Icons';
import {
  appendOrderHistory,
  selectSettings,
  setOrderHistory,
  setOrderHistoryError,
  setOrderHistoryLoading,
} from '../../redux/features/platformSlice';
import ConfirmationModal from './ConfirmationModal';
import EmptyState from './EmptyState';
import HistoryRow from './HistoryRow';
import ModifyOrderModal from './ModifyOrderModal';
import OrderDetailModal from './OrderDetailModal';
import PortfolioTabs from './PortfolioTabs';
import TradeRow from './TradeRow';
import {
  getOrderKey,
  getTicketValue,
  HISTORY_PAGE_SIZE,
  isBalanceHistoryDeal,
  withCalculatedHistoryProfit,
} from './portfolioUtils';

const HISTORY_DAY_OPTIONS = [
  { label: '7D', value: 7 },
  { label: '30D', value: 30 },
  { label: '90D', value: 90 },
  { label: '1Y', value: 365 },
  { label: 'All', value: 3650 },
];

const getItems = (response) => {
  if (Array.isArray(response?.data)) return response.data;
  if (Array.isArray(response?.data?.items)) return response.data.items;
  if (Array.isArray(response?.items)) return response.items;
  return [];
};

const normalizeFilterText = value => String(value || '').trim().toLowerCase();

const matchesHistoryFilters = (order, filters) => {
  const symbol = normalizeFilterText(filters.symbol);
  const orderQuery = normalizeFilterText(filters.order);
  const type = filters.type;

  if (symbol && !String(order?.symbol || '').toLowerCase().includes(symbol)) {
    return false;
  }

  if (orderQuery) {
    const values = [
      order?.deal,
      order?.ticket,
      order?.order,
      order?.positionID,
      order?.positionId,
      order?.position,
      order?.position_id,
    ].map(value => String(value || '').toLowerCase());
    if (!values.some(value => value.includes(orderQuery))) return false;
  }

  if (type === 'balance') return Boolean(order.isBalanceHistory || isBalanceHistoryDeal(order));
  if (type === 'buy' || type === 'sell') {
    const side = String(order?.side ?? order?.order_type ?? order?.orderType ?? '').toLowerCase();
    return side.includes(type);
  }

  return true;
};

const Portfolio = () => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const settings = useSelector(selectSettings);
  const positions = useSelector(s => s.platform.positions);
  const pendingOrders = useSelector(s => s.platform.pendingOrders);
  const history = useSelector(s => s.platform.orderHistory);
  const historyLoading = useSelector(s => s.platform.orderHistoryLoading);
  const historyError = useSelector(s => s.platform.orderHistoryError);

  const [tab, setTab] = useState('positions');
  const [modifyingOrder, setModifyingOrder] = useState(null);
  const [orderToClose, setOrderToClose] = useState(null);
  const [detailState, setDetailState] = useState({ order: null, state: null });
  const [historyFiltersOpen, setHistoryFiltersOpen] = useState(false);
  const [historyFilters, setHistoryFilters] = useState({
    symbol: '',
    order: '',
    type: 'all',
    days: 3650,
  });
  const [historyLimit, setHistoryLimit] = useState(HISTORY_PAGE_SIZE);
  const [historyHasMore, setHistoryHasMore] = useState(true);
  const historyFetchId = useRef(0);

  const calculatedHistory = useMemo(
    () => withCalculatedHistoryProfit(history),
    [history]
  );

  const visibleHistory = useMemo(() => (
    calculatedHistory.filter(order => matchesHistoryFilters(order, historyFilters))
  ), [calculatedHistory, historyFilters]);

  const counts = useMemo(() => ({
    positions: positions.length,
    pending: pendingOrders.length,
    history: 0,
  }), [pendingOrders.length, positions.length]);

  useEffect(() => {
    if (tab !== 'history') return;

    const fetchId = ++historyFetchId.current;
    const fetchHistory = async () => {
      try {
        dispatch(setOrderHistoryLoading(true));
        const response = await platformService.getOrderHistory(historyLimit, historyFilters.days, {
          symbol: historyFilters.symbol,
          order: historyFilters.order,
          type: historyFilters.type === 'balance' ? 'all' : historyFilters.type,
        });
        if (fetchId !== historyFetchId.current) return;

        if (response?.success !== false && response?.status !== false) {
          const items = getItems(response);
          if (historyLimit <= HISTORY_PAGE_SIZE) {
            dispatch(setOrderHistory(items));
          } else {
            dispatch(appendOrderHistory(items));
          }
          setHistoryHasMore(items.length >= historyLimit);
        } else {
          dispatch(setOrderHistoryError(response?.message || 'Failed to fetch order history'));
        }
      } catch (error) {
        if (fetchId !== historyFetchId.current) return;
        console.error('Failed to fetch order history:', error);
        dispatch(setOrderHistoryError(error?.message || 'Failed to fetch order history'));
      }
    };

    fetchHistory();
  }, [dispatch, historyFilters, historyLimit, tab]);

  const handleTabChange = useCallback((nextTab) => {
    if (nextTab === tab) return;
    setTab(nextTab);
  }, [tab]);

  const handleListScroll = useCallback((event) => {
    if (tab !== 'history') return;
    if (historyLoading || !historyHasMore) return;

    const el = event.currentTarget;
    const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
    if (distanceFromBottom < 120) {
      setHistoryLimit(count => count + HISTORY_PAGE_SIZE);
    }
  }, [historyHasMore, historyLoading, tab]);

  const updateHistoryFilter = useCallback((key, value) => {
    setHistoryFilters(filters => ({ ...filters, [key]: value }));
    setHistoryLimit(HISTORY_PAGE_SIZE);
    setHistoryHasMore(true);
  }, []);

  const handleCloseOrder = useCallback((order) => {
    if (settings.confirmClose) setOrderToClose({...order, closeKind: 'order'});
    else closeOrder(order);
  }, [settings.confirmClose]);
  
  const handleClosePosition = useCallback((order) => {
    if (settings.confirmClose) setOrderToClose({...order, closeKind: 'position'});
    else closePosition(order);
  }, [settings.confirmClose]);


  const confirmClose = useCallback(() => {
    if (!orderToClose) return;
    if (orderToClose.closeKind === 'position') {
      closePosition(orderToClose);
    } else {
      closeOrder(orderToClose);
    }
    setOrderToClose(null);
  }, [orderToClose]);

  const openDetails = useCallback((order, state) => {
    setDetailState({ order, state });
  }, []);

  const positionsContent = useMemo(() => (
    positions.length === 0
      ? <EmptyState icon={ActivityIcon} text="No open positions" />
      : positions.map(order => (
        <TradeRow
          key={getOrderKey(order)}
          order={order}
          onEdit={setModifyingOrder}
          onCloseRequested={handleClosePosition}
          onDetail={openDetails}
        />
      ))
  ), [handleClosePosition, openDetails, positions]);

  const pendingOrdersContent = useMemo(() => (
    pendingOrders.length === 0
      ? <EmptyState icon={ClockIcon} text="No pending orders" />
      : pendingOrders.map(order => (
        <TradeRow
          key={getOrderKey(order)}
          order={order}
          onEdit={setModifyingOrder}
          onCloseRequested={handleCloseOrder}
          onDetail={openDetails}
        />
      ))
  ), [handleCloseOrder, openDetails, pendingOrders]);

  const historyContent = useMemo(() => {
    return (
      <>
        {historyFiltersOpen && (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, padding: '8px 12px', borderBottom: `1px solid ${theme.border}22`, background: theme.card }}>
            <input
              value={historyFilters.symbol}
              onChange={event => updateHistoryFilter('symbol', event.target.value)}
              placeholder="Symbol"
              style={{ minWidth: 92, flex: '1 1 100px', height: 30, border: `1px solid ${theme.border}`, borderRadius: 6, padding: '0 9px', background: theme.surface, color: theme.text, fontSize: 12, outline: 'none', fontFamily: theme.sans }}
            />
            <input
              value={historyFilters.order}
              onChange={event => updateHistoryFilter('order', event.target.value)}
              placeholder="Order"
              style={{ minWidth: 92, flex: '1 1 100px', height: 30, border: `1px solid ${theme.border}`, borderRadius: 6, padding: '0 9px', background: theme.surface, color: theme.text, fontSize: 12, outline: 'none', fontFamily: theme.sans }}
            />
            <select
              value={historyFilters.type}
              onChange={event => updateHistoryFilter('type', event.target.value)}
              style={{ height: 30, border: `1px solid ${theme.border}`, borderRadius: 6, padding: '0 8px', background: theme.surface, color: theme.text, fontSize: 12, outline: 'none', fontFamily: theme.sans }}
            >
              <option value="all">All types</option>
              <option value="buy">Buy</option>
              <option value="sell">Sell</option>
              <option value="balance">Balance</option>
            </select>
            <select
              value={historyFilters.days}
              onChange={event => updateHistoryFilter('days', Number(event.target.value))}
              style={{ height: 30, border: `1px solid ${theme.border}`, borderRadius: 6, padding: '0 8px', background: theme.surface, color: theme.text, fontSize: 12, outline: 'none', fontFamily: theme.sans }}
            >
              {HISTORY_DAY_OPTIONS.map(option => (
                <option key={option.value} value={option.value}>{option.label}</option>
              ))}
            </select>
          </div>
        )}
        {visibleHistory.length === 0 && !historyLoading && (
          <EmptyState icon={ClockIcon} text="No closed trades yet" />
        )}
        {visibleHistory.map(order => (
          <HistoryRow
            key={getOrderKey(order)}
            order={order}
            onDetail={openDetails}
          />
        ))}
        {historyLoading && (
          <div style={{ padding: '12px', textAlign: 'center', fontSize: 11, color: theme.textM }}>
            Loading history...
          </div>
        )}
        {!historyLoading && historyError && (
          <div style={{ padding: '12px', textAlign: 'center', fontSize: 11, color: theme.neg }}>
            {historyError}
          </div>
        )}
      </>
    );
  }, [
    historyError,
    historyFilters.days,
    historyFilters.order,
    historyFilters.symbol,
    historyFilters.type,
    historyFiltersOpen,
    historyLoading,
    openDetails,
    theme,
    updateHistoryFilter,
    visibleHistory,
  ]);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', background: theme.card, position: 'relative' }}>
      <PortfolioTabs
        activeTab={tab}
        onChange={handleTabChange}
        counts={counts}
        theme={theme}
        historyFiltersOpen={historyFiltersOpen}
        onToggleHistoryFilters={() => setHistoryFiltersOpen(open => !open)}
      />

      <div onScroll={handleListScroll} style={{ flex: 1, overflowY: 'auto' }}>
        {tab === 'positions' && positionsContent}
        {tab === 'pending' && pendingOrdersContent}
        {tab === 'history' && historyContent}
      </div>

      {modifyingOrder && (
        <ModifyOrderModal
          order={modifyingOrder}
          onClose={() => setModifyingOrder(null)}
        />
      )}

      {orderToClose && (
        <ConfirmationModal
          title="Confirm Close Order"
          message={`Are you sure you want to close ${orderToClose.symbol} ${getOrderTypeLabel(orderToClose)} order #${getTicketValue(orderToClose)}?`}
          onConfirm={confirmClose}
          onClose={() => setOrderToClose(null)}
        />
      )}

      {detailState.order && (
        <OrderDetailModal
          order={detailState.order}
          onClose={() => setDetailState({ order: null, state: null })}
          state={detailState.state}
        />
      )}
    </div>
  );
};

export default Portfolio;
